import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import MaintenanceChecker from './components/MaintenanceChecker';
import BroadcastToasts from './components/BroadcastToasts';
import AnnonceBandeau from './components/AnnonceBandeau';
import UpdateBanner from './components/UpdateBanner';
import Login from './components/Login';
import DashboardRouter from './components/DashboardRouter';
import PageLoader, { GlobalPageLoader } from './components/PageLoader';
import authService from './services/auth.service';
import './styles/Global.css';
// Dashboard.css est une feuille partagée (dashboard-container/card utilisés par
// plusieurs pages, dont le Planning). Avec le code splitting, elle doit être
// chargée globalement — sinon une page qui l'utilise sans l'importer casse
// si on y navigue en premier (ex: hauteur du planning non bornée → plus de scroll).
import './styles/Dashboard.css';

// Code splitting : chaque page est un chunk séparé, chargé à la première visite.
// Le bundle initial ne contient plus que le layout, le login et le dashboard.
const AdminPanel                  = lazy(() => import('./components/AdminPanel'));
const EntityPanel                 = lazy(() => import('./components/EntityPanel'));
const RolePanel                   = lazy(() => import('./components/RolePanel'));
const MaterialPanel               = lazy(() => import('./components/MaterialPanel'));
const MaterialDetailPage          = lazy(() => import('./components/MaterialDetailPage'));
const CategoryPanel               = lazy(() => import('./components/CategoryPanel'));
const ClientPanel                 = lazy(() => import('./components/ClientPanel'));
const ParcPanel                   = lazy(() => import('./components/ParcPanel'));
const EventsPage                  = lazy(() => import('./components/EventsPage'));
const SalonPage                   = lazy(() => import('./components/SalonPage'));
const DevToolPanel                = lazy(() => import('./components/DevToolPanel'));
const PatchNotesPage              = lazy(() => import('./components/PatchNotesPage'));
const RetoursPage                 = lazy(() => import('./components/RetoursPage'));
const PreparationLogistiquePanel  = lazy(() => import('./components/PreparationLogistiquePanel'));
const SuiviRetoursPanel           = lazy(() => import('./components/SuiviRetoursPanel'));
const ValidationDevisPanel        = lazy(() => import('./components/ValidationDevisPanel'));
const StatistiquesPanel           = lazy(() => import('./components/StatistiquesPanel'));
const RepartitionEntitePanel      = lazy(() => import('./components/RepartitionEntitePanel'));
const ProjetPanel                 = lazy(() => import('./components/ProjetPanel'));
const ProjetParametresPanel       = lazy(() => import('./components/ProjetParametresPanel'));
const ContactsPanel               = lazy(() => import('./components/ContactsPanel'));
const EquipePlanningPage          = lazy(() => import('./components/EquipePlanningPage'));
const PlanningManagerPage         = lazy(() => import('./components/PlanningManagerPage'));
/* ── EVOLIS (module détachable — voir EVOLIS-MODULE.md) ── */
const EvolisPage                  = lazy(() => import('./evolis/EvolisPage'));
/* ── fin EVOLIS ── */

/**
 * Portail de session : au tout premier rendu (après un chargement de page ou
 * un F5), on confirme l'identité auprès du serveur (/api/me) AVANT d'afficher
 * la sidebar et le contenu. Le localStorage est alors réécrit avec la vérité
 * serveur — impossible donc de « s'afficher » en admin en trafiquant
 * l'inspecteur : les menus ne se dessinent qu'une fois l'identité réelle
 * confirmée. La vérification bloquante n'a lieu qu'une fois par chargement ;
 * les navigations internes ne sont pas ralenties.
 */
let sessionVerifiee = false;
const SessionGate = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const doitVerifier = !sessionVerifiee && !isLoginPage && authService.isAuthenticated();
  const [pret, setPret] = useState(!doitVerifier);

  useEffect(() => {
    if (sessionVerifiee || isLoginPage || !authService.isAuthenticated()) {
      setPret(true);
      return undefined;
    }
    let annule = false;
    authService.syncSession().finally(() => {
      sessionVerifiee = true;
      if (!annule) setPret(true);
    });
    return () => { annule = true; };
  }, [isLoginPage]);

  if (!pret) return <PageLoader />;
  return children;
};

// Composant wrapper pour le layout avec sidebar
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  
  return (
    <div className="app-layout">
      {!isLoginPage && <Sidebar />}
      {!isLoginPage && <BroadcastToasts />}
      {/* Alerte « nouvelle version » après un déploiement (bouton Mettre à jour) */}
      <UpdateBanner />
      <main className={`app-content ${isLoginPage ? 'full-width' : ''}`}>
        {!isLoginPage && <AnnonceBandeau />}
        {children}
        {/* Écran de chargement unique : recouvre le contenu tant qu'un
            chargement (code de module ou données de page) est en cours. */}
        {!isLoginPage && <GlobalPageLoader />}
      </main>
    </div>
  );
};

function App() {
  // Préchargement en tâche de fond des modules les plus lourds/fréquents,
  // quelques secondes après l'arrivée (le temps que la première page soit
  // installée). Le loader plein écran du code splitting n'apparaît alors
  // quasiment plus : il ne reste que le chargement des données de la page.
  useEffect(() => {
    const t = setTimeout(() => {
      import('./components/EventsPage');
      import('./components/PlanningManagerPage');
      import('./components/EquipePlanningPage');
      import('./components/ProjetPanel');
      import('./components/SalonPage');
      import('./components/MaterialPanel');
      import('./components/StatistiquesPanel');
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  return (
    <Router>
      <SessionGate>
      <AppLayout>
        <MaintenanceChecker>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Page de login - pas protégée */}
            <Route path="/login" element={<Login />} />
            
            {/* Routes protégées par accès */}
            <Route path="/" element={
              <ProtectedRoute><DashboardRouter /></ProtectedRoute>
            } />
            <Route path="/admin" element={<Navigate to="/admin/events" replace />} />
            <Route path="/admin/users" element={
              <ProtectedRoute><AdminPanel /></ProtectedRoute>
            } />
            <Route path="/admin/entities" element={
              <ProtectedRoute><EntityPanel /></ProtectedRoute>
            } />
            <Route path="/admin/roles" element={
              <ProtectedRoute><RolePanel /></ProtectedRoute>
            } />
            <Route path="/admin/materials" element={
              <ProtectedRoute><MaintenanceChecker><MaterialPanel /></MaintenanceChecker></ProtectedRoute>
            } />
            <Route path="/admin/materials/:id" element={
              <ProtectedRoute><MaintenanceChecker><MaterialDetailPage /></MaintenanceChecker></ProtectedRoute>
            } />
            <Route path="/admin/categories" element={
              <ProtectedRoute><CategoryPanel /></ProtectedRoute>
            } />
            <Route path="/admin/clients" element={
              <ProtectedRoute><ClientPanel /></ProtectedRoute>
            } />
            <Route path="/admin/parcs" element={
              <ProtectedRoute><ParcPanel /></ProtectedRoute>
            } />
            <Route path="/admin/events" element={
              <ProtectedRoute><EventsPage /></ProtectedRoute>
            } />
            <Route path="/admin/salons" element={
              <ProtectedRoute><MaintenanceChecker><SalonPage /></MaintenanceChecker></ProtectedRoute>
            } />
            <Route path="/admin/devtool" element={
              <ProtectedRoute><DevToolPanel /></ProtectedRoute>
            } />
            <Route path="/nouveautes" element={
              <ProtectedRoute><PatchNotesPage /></ProtectedRoute>
            } />
            <Route path="/retours" element={
              <ProtectedRoute><RetoursPage /></ProtectedRoute>
            } />
            <Route path="/admin/preparation" element={
              <ProtectedRoute><PreparationLogistiquePanel /></ProtectedRoute>
            } />
            <Route path="/admin/suivi-retours" element={
              <ProtectedRoute><SuiviRetoursPanel /></ProtectedRoute>
            } />
            <Route path="/admin/validation-devis" element={
              <ProtectedRoute><ValidationDevisPanel /></ProtectedRoute>
            } />
            <Route path="/admin/statistiques" element={
              <ProtectedRoute><StatistiquesPanel /></ProtectedRoute>
            } />
            <Route path="/admin/repartition-entites" element={
              <ProtectedRoute><RepartitionEntitePanel /></ProtectedRoute>
            } />
            <Route path="/admin/projets" element={
              <ProtectedRoute><MaintenanceChecker><ProjetPanel /></MaintenanceChecker></ProtectedRoute>
            } />
            <Route path="/admin/projets-parametres" element={
              <ProtectedRoute><ProjetParametresPanel /></ProtectedRoute>
            } />
            <Route path="/admin/contacts" element={
              <ProtectedRoute><ContactsPanel /></ProtectedRoute>
            } />
            <Route path="/admin/equipe-planning" element={
              <ProtectedRoute><EquipePlanningPage /></ProtectedRoute>
            } />
            <Route path="/admin/planning-manager" element={
              <ProtectedRoute><PlanningManagerPage /></ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardRouter /></ProtectedRoute>
            } />
            {/* ── EVOLIS ── */}
            <Route path="/evolis" element={
              <ProtectedRoute><EvolisPage /></ProtectedRoute>
            } />
            {/* ── fin EVOLIS ── */}
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </MaintenanceChecker>
      </AppLayout>
      </SessionGate>
    </Router>
  );
}

export default App;
